import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private onlyNumbers = new RegExp('^[0-9]+$');

  constructor() {}

  public onlyHasNumbers(value): boolean {
    return this.onlyNumbers.test(value);
  }

  /**
   * Checks if a JavaScript value is empty
   * @example
   *    isEmpty(null); // true
   *    isEmpty(undefined); // true
   *    isEmpty(''); // true
   *    isEmpty([]); // true
   *    isEmpty({}); // true
   * @param {any} value - item to test
   * @returns {boolean} true if empty, otherwise false
   */
  public isEmpty(value): boolean {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && Object.keys(value).length === 0)
    );
  }

  public getCurrentDayFormatted() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}_${month}_${year}`;
  }

  public isValidEmail(email: string): boolean {
    return Boolean(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        String(email).toLowerCase()
      )
    );
  }

  public isValidPassword(password: string): boolean {
    return Boolean(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        String(password)
      )
    );
  }

  public isValidUrl(url: string) {
    return Boolean(
      /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
        String(url)
      )
    );
  }

  public isValidImagePreview(value: string): boolean {
    const trimmed = String(value).trim();
    if (trimmed.startsWith('data:image/')) {
      return true;
    }
    if (trimmed.startsWith('blob:')) {
      return true;
    }
    return this.isValidUrl(trimmed);
  }

  /**
   * Converts a string into a URL-friendly slug.
   *
   * This function normalizes the string according to the following rules:
   * 1.  Handles non-string inputs gracefully.
   * 2.  Removes diacritics from international characters (e.g., `é` becomes `e`).
   * 3.  Converts the string to lowercase.
   * 4.  Trims leading and trailing whitespace.
   * 5.  Removes all characters that are not letters, numbers, spaces, or hyphens.
   * 6.  Replaces one or more consecutive spaces with a single hyphen.
   * 7.  Collapses multiple consecutive hyphens into a single one.
   * 8.  Trims any leading or trailing hyphens from the final result.
   *
   * @param name The string to be slugified, typically a venue title.
   * @returns The slugified string.
   *
   * @example
   * // returns "my-awesome-group-link"
   * slugify("  My Awesome Group Link!!  ");
   *
   * @example
   * // returns "lhotel-cote-dazur"
   * slugify("L'hôtel Côte d'Azur");
   *
   * @example
   * // returns "a-b"
   * slugify("--- A   B ---");
   */
  public slugifyName(name: string): string {
    if (typeof name !== 'string') {
      return '';
    }

    const slug = name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

      .toLowerCase()

      .trim()

      .replace(/[^a-z0-9\s-]/g, '')

      .replace(/\s+/g, '-')

      .replace(/-+/g, '-');

    return slug.replace(/^-+|-+$/g, '');
  }
}
